import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Button,
  Typography,
  FormControlLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
} from '@mui/material';

const UserManagement = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [users, setUsers] = useState([]);
  const [columns, setColumns] = useState({
    username: true,
    email: true,
    role: true,
    name: true,
  });
  const [roles, setRoles] = useState([]);
  const [editingUserId, setEditingUserId] = useState(null);
  const [editedUser, setEditedUser] = useState({});
  const [fetchToggle, setFetchToggle] = useState(0);
  const [openRoleDialog, setOpenRoleDialog] = useState(false);
  const [newRole, setNewRole] = useState("");

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/users/all`);
        setUsers(response.data);
      } catch (error) {
        enqueueSnackbar('Failed to fetch users', { variant: 'error' });
      }
    };
    fetchUsers();
  }, [fetchToggle, enqueueSnackbar]);

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/roles/all`);
      setRoles(response.data);
    } catch (error) {
      enqueueSnackbar('Failed to fetch roles', { variant: 'error' });
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  const toggleColumn = (column) => {
    setColumns((prev) => ({ ...prev, [column]: !prev[column] }));
  };

  const handleEditClick = (user) => {
    setEditingUserId(user._id);
    setEditedUser(user);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedUser((prev) => ({ ...prev, [name]: value }));
  };

  const handleRoleChange = (event) => {
    const { value } = event.target;
    setEditedUser((prev) => ({ ...prev, role: value }));
  };

  const handleSaveClick = async () => {
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/users/${editingUserId}`, editedUser);
      setFetchToggle((prev) => !prev);
      setEditingUserId(null);
      enqueueSnackbar('User updated successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Error updating user', { variant: 'error' });
    }
  };

  const handleCancelClick = () => {
    setEditingUserId(null);
    setEditedUser({});
  };

  const handleDeleteClick = async (userId) => {
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/users/${userId}`);
      setFetchToggle((prev) => !prev);
      enqueueSnackbar('User deleted successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Error deleting user', { variant: 'error' });
    }
  };

  const handleOpenRoleDialog = () => setOpenRoleDialog(true);
  const handleCloseRoleDialog = () => setOpenRoleDialog(false);

  const handleAddRole = async () => {
    if (newRole.trim() === "") {
      enqueueSnackbar('Role cannot be empty', { variant: 'warning' });
      return;
    }
    console.log(newRole)
  
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/roles/add`, {
        name: newRole.trim(),
      });
      enqueueSnackbar(`Role "${newRole}" added successfully`, { variant: 'success' });
      fetchRoles();
      setNewRole("");
      handleCloseRoleDialog();
    } catch (error) {
      console.error('Error adding role:', error);
      enqueueSnackbar('Error adding role.', { variant: 'error' });
    }
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="center" padding={3} style={{ height: '100vh', width: '100vw' }}>
      <Paper elevation={3} style={{ padding: '32px', backgroundColor: '#f5f5f5', width: '100%', height: '100%' }}>
        <Typography variant="h4" gutterBottom align="center">
          User Management
        </Typography>

        <Typography variant="h6" gutterBottom align="center" style={{ marginBottom: '16px' }}>
          Select Columns
        </Typography>
        <Box display="flex" flexWrap="wrap" justifyContent="center" alignItems="center" marginBottom={2}>
          {Object.keys(columns).map((column) => (
            <FormControlLabel
              key={column}
              control={<Checkbox checked={columns[column]} onChange={() => toggleColumn(column)} color="primary" />}
              label={column.charAt(0).toUpperCase() + column.slice(1)}
              style={{ margin: '0 16px', fontWeight: 'bold', color: '#333' }}
            />
          ))}
        </Box>

        <Button variant="contained" color="primary" onClick={handleOpenRoleDialog} style={{ marginBottom: '16px' }}>
          Add New Role
        </Button>

        <TableContainer component={Paper} style={{ marginTop: '16px', width: '100%' }}>
          <Table>
            <TableHead>
              <TableRow>
                {columns.username && <TableCell>Username</TableCell>}
                {columns.name && <TableCell>Name</TableCell>}
                {columns.email && <TableCell>Email</TableCell>}
                {columns.role && <TableCell>Role</TableCell>}
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user._id}>
                  {columns.username && (
                    <TableCell>
                      {editingUserId === user._id ? (
                        <TextField name="username" value={editedUser.username} onChange={handleInputChange} fullWidth />
                      ) : (
                        user.username
                      )}
                    </TableCell>
                  )}
                  {columns.name && (
                    <TableCell>
                      {editingUserId === user._id ? (
                        <TextField name="name" value={editedUser.name} onChange={handleInputChange} fullWidth />
                      ) : (
                        user.name
                      )}
                    </TableCell>
                  )}
                  {columns.email && (
                    <TableCell>
                      {editingUserId === user._id ? (
                        <TextField name="email" value={editedUser.email} onChange={handleInputChange} fullWidth />
                      ) : (
                        user.email
                      )}
                    </TableCell>
                  )}
                  {columns.role && (
                    <TableCell>
                      {editingUserId === user._id ? (
                        <Select name="role" value={editedUser.role} onChange={handleRoleChange} fullWidth>
                          {roles.map((role) => (
                            <MenuItem key={role} value={role}>
                              {role.name}
                            </MenuItem>
                          ))}
                        </Select>
                      ) : (
                        user.role.name
                      )}
                    </TableCell>
                  )}
                  <TableCell>
                    {editingUserId === user._id ? (
                      <>
                        <Button onClick={handleSaveClick} color="success" variant="contained">
                          Save
                        </Button>
                        <Button onClick={handleCancelClick} color="error" variant="outlined">
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button onClick={() => handleEditClick(user)} color="primary" variant="outlined">
                          Edit
                        </Button>
                        <Button onClick={() => handleDeleteClick(user._id)} color="error" variant="contained">
                          Delete
                        </Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Dialog for adding new role */}
      <Dialog open={openRoleDialog} onClose={handleCloseRoleDialog}>
        <DialogTitle>Add New Role</DialogTitle>
        <DialogContent>
          <TextField label="Role Name" fullWidth value={newRole} onChange={(e) => setNewRole(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAddRole} color="primary">
            Add
          </Button>
          <Button onClick={handleCloseRoleDialog} color="secondary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
